<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import CatalogProductSetsAPI from 'dashboard/api/catalogProductSets';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';

const props = defineProps({
  filters: { type: Object, required: true },
});

const { t } = useI18n();
const products = ref([]);
const loading = ref(false);
const total = ref(0);
const currentPage = ref(1);
const perPage = 10;
const debounceTimer = ref(null);

const hasRules = computed(() => (props.filters?.rules?.length ?? 0) > 0);

const fetchPreview = async (page = 1) => {
  if (!hasRules.value) { products.value = []; total.value = 0; return; }
  try {
    loading.value = true;
    const { data } = await CatalogProductSetsAPI.preview(props.filters, page);
    products.value = data.products || [];
    total.value = data.total || 0;
    currentPage.value = data.page || page;
  } finally {
    loading.value = false;
  }
};

watch(() => props.filters, () => {
  clearTimeout(debounceTimer.value);
  debounceTimer.value = setTimeout(() => fetchPreview(1), 400);
}, { deep: true, immediate: true });
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-center justify-between">
      <span class="text-sm font-medium text-n-slate-12">{{ t('CATALOG.PRODUCT_SETS.PREVIEW.TITLE') }}</span>
      <span v-if="!loading && hasRules" class="text-xs text-n-slate-10">
        {{ total }} {{ t('CATALOG.PRODUCTS.COUNT') }}
      </span>
    </div>

    <div v-if="!hasRules" class="py-8 text-center text-sm text-n-slate-10">
      {{ t('CATALOG.PRODUCT_SETS.PREVIEW.ADD_FILTER_HINT') }}
    </div>

    <div v-else-if="loading" class="flex justify-center py-8">
      <Spinner />
    </div>

    <template v-else-if="products.length">
      <div class="border border-n-weak rounded-xl overflow-hidden">
        <table class="w-full text-sm">
          <thead class="bg-n-solid-2 border-b border-n-weak">
            <tr class="text-n-slate-10 text-xs">
              <th class="px-3 py-3 text-left font-medium w-10" />
              <th class="px-3 py-3 text-left font-medium">{{ t('CATALOG.PRODUCTS.VARIANT_HEADERS.TITLE') }}</th>
              <th class="px-3 py-3 text-left font-medium">{{ t('CATALOG.PRODUCTS.FILTER.SOURCE_LABEL') }}</th>
              <th class="px-3 py-3 text-left font-medium">{{ t('CATALOG.PRODUCTS.FILTER.STATUS_LABEL') }}</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-n-weak">
            <tr v-for="product in products" :key="product.id">
              <td class="px-3 py-3">
                <img v-if="product.image_url" :src="product.image_url" :alt="product.title" class="size-8 rounded object-cover" />
                <div v-else class="size-8 rounded bg-n-solid-3 flex items-center justify-center">
                  <i class="i-lucide-image text-n-slate-9 text-xs" />
                </div>
              </td>
              <td class="px-3 py-3 text-n-slate-12 font-medium">{{ product.title }}</td>
              <td class="px-3 py-3 text-n-slate-10 text-xs">{{ product.source?.kind }}</td>
              <td class="px-3 py-3">
                <span class="text-[10px] px-2 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11">{{ product.status }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="[&>div]:!border-t-0">
        <PaginationFooter
          v-if="total > perPage"
          :current-page="currentPage"
          :total-items="total"
          :items-per-page="perPage"
          @update:current-page="fetchPreview"
        />
      </div>
    </template>

    <div v-else class="py-8 text-center text-sm text-n-slate-10">
      {{ t('CATALOG.PRODUCT_SETS.PREVIEW.EMPTY') }}
    </div>
  </div>
</template>
